import React from 'react'
import { Section } from '@shared/components/section'
import styled from 'styled-components'

export const BlockDealConditions = ({
	blockTitle,
	data,
}: BlockDealConditionsProps): JSX.Element => {
	const { loanerTitle, loanerText, pledgeTitle, pledgeText, loanerItems, pledgeItems } = data || {}
	const hasLoaner = !!(loanerTitle || loanerText || loanerItems?.length)
	const hasPledge = !!(pledgeTitle || pledgeText || pledgeItems?.length)
	return (
		<ViewRoot>
			<Section>
				{!!blockTitle && <TextHeader>{blockTitle}</TextHeader>}
				<ViewRow>
					{hasLoaner && (
						<ViewColLoaner>
							{loanerTitle && <TextColHeaderLoaner>{loanerTitle}</TextColHeaderLoaner>}
							{loanerText && <Text>{loanerText}</Text>}
							{!!loanerItems?.length && (
								<List>
									{loanerItems.map((loanerItem, idx) => (
										<li key={idx}>{loanerItem}</li>
									))}
								</List>
							)}
						</ViewColLoaner>
					)}
					{hasPledge && (
						<ViewColPledge>
							{pledgeTitle && <TextColHeaderPledge>{pledgeTitle}</TextColHeaderPledge>}
							{pledgeText && <Text>{pledgeText}</Text>}
							{!!pledgeItems?.length && (
								<ViewPledgeRow>
									{pledgeItems.map((pledgeItem, idx) => (
										<div key={idx}>
											{!!pledgeItem.title && <TextListTitle>{pledgeItem.title}</TextListTitle>}
											{!!pledgeItem.items?.length && (
												<List>
													{pledgeItem.items.map((item, idx) => (
														<li key={idx}>{item}</li>
													))}
												</List>
											)}
										</div>
									))}
								</ViewPledgeRow>
							)}
						</ViewColPledge>
					)}
				</ViewRow>
			</Section>
		</ViewRoot>
	)
}

const TextListTitle = styled.h4(({ theme }) => ({
	...theme.typography.header16_22,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header18_24,
	},
}))

const ViewPledgeRow = styled.div(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	flexDirection: 'column',
	marginTop: 20,
	gap: '30px 20px',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		flexDirection: 'row',
	},
	'& > div': {
		flex: 'calc(50% - 10px)',
	},
}))

const List = styled.ul(({ theme }) => ({
	paddingLeft: 24,
	marginTop: 20,
	...theme.typography.text16_22,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
	},
	'& li': {
		marginTop: 10,
		listStyleType: 'disc',
	},
}))

const TextColHeaderLoaner = styled.h3(({ theme }) => ({
	marginBottom: 10,
	...theme.typography.header18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header24_32,
	},
}))

const TextColHeaderPledge = styled.h3(({ theme }) => ({
	marginBottom: 20,
	...theme.typography.header18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header24_32,
	},
}))

const Text = styled.p(({ theme }) => ({
	...theme.typography.text16_22,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
	},
}))

const ViewColPledge = styled.div(({ theme }) => ({
	backgroundColor: theme.colors.mainwhite,
	borderRadius: 8,
	padding: 20,
	flex: 2,
}))

const ViewColLoaner = styled.div(({ theme }) => ({
	backgroundColor: theme.colors.mainwhite,
	borderRadius: 8,
	padding: 20,
	flex: 1,
}))

const ViewRow = styled.div(({ theme }) => ({
	display: 'flex',
	gap: '20px 32px',
	flexDirection: 'column',
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		flexDirection: 'row',
	},
}))

const TextHeader = styled.h2(({ theme }) => ({
	marginBottom: 40,
	...theme.typography.header24_28,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header40_44,
	},
}))

const ViewRoot = styled.div(({ theme }) => ({
	backgroundColor: theme.colors.pacificblue,
	padding: '60px 0',
	marginBottom: 60,
}))

interface BlockDealConditionsItem {
	title?: string | undefined
	items?: Array<string> | undefined
}

export interface BlockDealConditionsData {
	loanerTitle?: string | undefined
	loanerText?: string | undefined
	loanerItems?: Array<string> | undefined
	pledgeTitle?: string | undefined
	pledgeText?: string | undefined
	pledgeItems?: Array<BlockDealConditionsItem> | undefined
}

interface BlockDealConditionsProps {
	blockTitle?: string | undefined
	data?: BlockDealConditionsData
}
